/**
 * **IMPACT 的粒子：从画面正中炸出去的一把碎三角。**
 *
 * 评审：
 *
 * > 添加一种濒死 IMPACT 粒子效果：生成后，以较高的初速度和较高的衰减自屏幕
 * > 中心向随机方向飞去，z-index 在漂浮于屏幕中心的异常牌之下。
 * > 生成时为每个粒子绘制一个不同的随机形状的 2D 三角形作为粒子贴图，且绘制的
 * > 样式在以下几种中随机选择：无填充白色描边 + 红色外发光 / 无填充红色描边 +
 * > 白色外发光 / 白色填充无描边 / 红色填充无描边。
 * > 生成粒子时，分两批生成【数个】【稍大】且【稍慢】的粒子和【较多】、【较小】
 * > 且【较快】的粒子，两个批次同时生成，没有时间差。
 *
 * 第二轮又调了两处（评审："粒子的旋转可以设置的少一些，并且最好也有衰减。
 * 并且我希望粒子在后半段生命中旋转和移动都是基本停滞的状态"）：
 *
 * - 自转幅度砍到 ±2.5 rad/s（原来 ±7），**和位移共用同一条速度包络**
 * - 衰减不再是指数拖尾，而是按**寿命归一化**的包络：`(1-u)^(STALL_POWER-1)`。
 *   它前段几乎不减（"炸出去"那一下还在），到 u=0.5 只剩 3%、u=0.7 剩 0.2%
 *   —— 后半段就是"定在半空慢慢淡掉"，而不是原来那条一直飘的指数尾巴。
 *   代价是总射程 = 初速 × 寿命 ÷ `STALL_POWER`（初速不变，见 `travelSpeed`）。
 *
 * 第三轮又补了一条："大多数时候粒子效果都是 100% 的 opacity，直至最后一点点生命时
 * 再消失掉" —— 淡出窗口从"后 45%"收到最后 15%（`FADE_FROM`）。
 *
 * ## 为什么是"贴图"而不是 Graphics
 *
 * 五十来个粒子，每个形状都不一样：用 `Graphics` 就是每帧五十次路径三角化，
 * 而且**外发光**（`shadowBlur`）在 Pixi 里只有 canvas 上有 —— 我们用的是
 * **canvas 2D 烘一张小贴图**，发光是 `ctx.shadowBlur/shadowColor` 画的，
 * 画完就是一张 `Texture`，之后每帧只是摆 `Sprite` 的位置/旋转/透明度。
 *
 * ## 随机用 `Math.random`
 *
 * 纯视效、不需要可复现（见 `docs/onboarding.md` §4.4 第 5 条）：这一把碎屑
 * 往哪儿飞、长哪个形状，对玩家和测试都没有意义。
 */

import { Container, DOMAdapter, Sprite, Texture } from 'pixi.js';
import { LIGHT, PRESS } from '../palette';

/** 一颗粒子的参数（纯数据，便于测试） */
export interface ParticleSpec {
  /** 初速度（逻辑像素/秒） */
  readonly speed: number;
  /** 尺寸（逻辑像素，三角形外接圆的直径量级） */
  readonly size: number;
  /** 寿命（秒） */
  readonly life: number;
}

/**
 * **两批粒子的参数。**
 *
 * 评审要的是"同时生成、没有时间差"的两批：**数个稍大稍慢** + **较多较小较快**。
 * 数量差（8 vs 46）不只是"多"：大块头是"看得清的那几块"，小块头是"雾"。
 */
/**
 * ⚠️ **寿命 2 秒左右**（评审第一版之后："粒子消失得过快，它应当有更长的寿命，
 * 例如 2 秒，同时保持目前的运动速度"）—— `speed` 一个字没动，动的是 `life`
 * （0.6–1.1s → 1.7–2.2s）。
 *
 * ⚠️ 减速**不在这一层**：所有粒子共用 `travelSpeed()` 那条按寿命归一化的包络，
 * 所以这里没有 `drag` 可拧。要"更早停住"就调 `STALL_POWER`。
 */
export const IMPACT_PARTICLES = {
  /** 大的那几块：少、大、慢 */
  big: { count: 8, speed: [340, 620], size: [22, 40], life: [1.8, 2.2] },
  /** 小的那一把：多、小、快 */
  small: { count: 46, speed: [900, 1500], size: [6, 14], life: [1.7, 2.1] },
} as const;

/**
 * **"停下来的形状"**：速度 = 初速 × `(1-u)^(STALL_POWER-1)`，`u` 是**已过的寿命比**。
 *
 * 为什么不用指数衰减：指数尾巴永远不为零 —— 4 秒长的寿命里，到最后粒子还在
 * 慢慢挪，看着像"飘"，不像"炸完定住"。按寿命归一化才能在**半程**就基本停死：
 *
 * | u（寿命比） | 速度还剩 |
 * | --- | --- |
 * | 0.3 | 17% |
 * | 0.5 | 3.1% |
 * | 0.7 | 0.24% |
 *
 * 位移是它的积分，所以**总射程 = 初速 × 寿命 ÷ `STALL_POWER`**（初速本身不变，
 * 即 u=0 处仍是设定的那个速度）。调大 = 停得更早、飞得更近。
 */
export const STALL_POWER = 6;

/** 自转幅度的上限（rad/s，正负对称）。评审："旋转可以设置的少一些" */
export const SPIN_RANGE = 5;

/** 烘多少张不同的三角形贴图（每个粒子从中随机取一张） */
const SHAPE_POOL = 26;
/** 贴图边长（物理像素）。够画一个带发光的三角形，多了白花显存 */
const SHAPE_PX = 64;

/**
 * **一颗粒子出生时的运动参数**（纯函数，可测）。
 *
 * @param random 注入 `[0,1)`（测试里给确定性的那个）
 */
export function spawnMotion(
  spec: {
    readonly speed: readonly [number, number];
    readonly size: readonly [number, number];
    readonly life: readonly [number, number];
  },
  random: () => number = Math.random,
): { vx: number; vy: number; size: number; spin: number; life: number } {
  const span = (range: readonly [number, number]): number =>
    range[0] + random() * (range[1] - range[0]);
  const angle = random() * Math.PI * 2;
  const speed = span(spec.speed);
  return {
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    size: span(spec.size),
    spin: (random() - 0.5) * SPIN_RANGE,
    life: span(spec.life),
  };
}

/**
 * **速度包络**（纯函数）：给定"已过的寿命比" `u`，返回初速还剩几成。
 *
 * 位移和自转**共用这一条** —— 评审要的正是"后半段旋转和移动都基本停滞"，
 * 两条曲线分开调反而会让它俩错位。
 */
export function travelSpeed(u: number): number {
  const t = Math.min(1, Math.max(0, u));
  return (1 - t) ** (STALL_POWER - 1);
}

/**
 * **一颗粒子的透明度**（纯函数）。
 *
 * 评审第二轮："希望大多数时候粒子效果都是 100% 的 opacity，直至最后一点点生命时
 * 再消失掉" —— 所以前 85% 寿命**全程全亮**（它们这时候已经基本停住了，就是定在
 * 半空的一把碎屑），最后 15% 才线性淡到 0。
 *
 * ⚠️ 一出来就淡会显得"没炸开" —— 那一下得先看得见；反过来拖到最后一帧再"啪"地
 *    消失又会像掉帧。留 15% 是那个"散掉"的尾巴。
 */
export const FADE_FROM = 0.85;

export function particleAlpha(t: number): number {
  if (t <= FADE_FROM) return 1;
  return Math.max(0, 1 - (t - FADE_FROM) / (1 - FADE_FROM));
}

/** 把一条随机三角形烘成贴图。四种样式按评审那四条随机挑 */
function bakeTriangle(): Texture {
  const canvas = DOMAdapter.get().createCanvas(SHAPE_PX, SHAPE_PX);
  const ctx = canvas.getContext('2d');
  if (ctx === null) return Texture.WHITE;
  const white = `#${LIGHT.base.toString(16).padStart(6, '0')}`;
  const red = `#${PRESS.red.toString(16).padStart(6, '0')}`;
  const pad = 10;
  const span = SHAPE_PX - pad * 2;
  // 三角形：三个随机点（夹在安全区里，别被贴图边裁掉）
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 3; i++) {
    pts.push([pad + Math.random() * span, pad + Math.random() * span]);
  }
  const path = (): void => {
    ctx.beginPath();
    ctx.moveTo(pts[0]?.[0] ?? 0, pts[0]?.[1] ?? 0);
    ctx.lineTo(pts[1]?.[0] ?? 0, pts[1]?.[1] ?? 0);
    ctx.lineTo(pts[2]?.[0] ?? 0, pts[2]?.[1] ?? 0);
    ctx.closePath();
  };
  const style = Math.floor(Math.random() * 4);
  ctx.lineJoin = 'round';
  if (style === 0 || style === 1) {
    // 无填充 + 描边 + 外发光（发光颜色是描边的互补）
    const stroke = style === 0 ? white : red;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 3;
    ctx.shadowColor = style === 0 ? red : white;
    ctx.shadowBlur = 12;
    path();
    // 描两遍：第一遍把发光铺开，第二遍让边缘实一点
    ctx.stroke();
    ctx.stroke();
  } else {
    // 实心，无描边
    ctx.fillStyle = style === 2 ? white : red;
    path();
    ctx.fill();
  }
  return Texture.from(canvas);
}

interface Live {
  readonly sprite: Sprite;
  /** **初**速度（逻辑像素/秒）。每帧由 `travelSpeed()` 缩放出当时的速度 */
  readonly vx: number;
  readonly vy: number;
  /** **初**自转角速度（rad/s）。和位移共用同一条包络 */
  readonly spin: number;
  age: number;
  readonly life: number;
}

/**
 * **一把粒子。**
 *
 * 用法（对局屏）：
 *
 * ```ts
 *   const particles = new ImpactParticles();
 *   container.addChild(particles.view);      // ⚠️ 加在**牌那一层之前**（它在牌下面）
 *   container.addChild(effects);             // 牌 / 翻牌 / 正中那张牌
 *   // 打击那一刻
 *   particles.burst(center);
 *   // 每帧
 *   particles.update(ticker.deltaMS);
 * ```
 */
export class ImpactParticles {
  /** 加进场景的东西。**要加在"牌"那一层之前**（粒子在牌下面） */
  readonly view: Container;

  readonly #pool: Texture[] = [];
  readonly #live: Live[] = [];
  readonly #sprites: Sprite[] = [];
  #baked = false;

  constructor() {
    this.view = new Container();
    this.view.label = 'impact-particles';
    // ⚠️ 不吃指针：它是纯装饰，盖在背景上但牌还能点
    this.view.eventMode = 'none';
  }

  /** 现在活着几颗（冒烟 / 调试读数用） */
  get count(): number {
    return this.#live.length;
  }

  /** 已经烘了几张贴图（调试读数用） */
  get shapes(): number {
    return this.#pool.length;
  }

  /**
   * **炸一把。** 两批**同时**生成（评审："两个批次同时生成，没有时间差"）。
   *
   * @param at 爆炸中心（逻辑画布坐标）—— 一般是画面正中
   */
  burst(at: { x: number; y: number }): void {
    if (!this.#baked) {
      for (let i = 0; i < SHAPE_POOL; i++) this.#pool.push(bakeTriangle());
      this.#baked = true;
    }
    this.#spawnBatch(IMPACT_PARTICLES.big, at);
    this.#spawnBatch(IMPACT_PARTICLES.small, at);
  }

  #spawnBatch(
    spec: {
      readonly count: number;
      readonly speed: readonly [number, number];
      readonly size: readonly [number, number];
      readonly life: readonly [number, number];
    },
    at: { x: number; y: number },
  ): void {
    for (let i = 0; i < spec.count; i++) {
      const sprite = this.#takeSprite();
      const texture = this.#pool[Math.floor(Math.random() * this.#pool.length)];
      if (texture !== undefined) sprite.texture = texture;
      const motion = spawnMotion(spec);
      sprite.width = motion.size;
      sprite.height = motion.size;
      sprite.position.set(at.x, at.y);
      sprite.rotation = Math.random() * Math.PI * 2;
      sprite.alpha = 1;
      sprite.visible = true;
      this.#live.push({
        sprite,
        vx: motion.vx,
        vy: motion.vy,
        spin: motion.spin,
        age: 0,
        life: motion.life,
      });
    }
  }

  /** 从回收站里拿一个 `Sprite`（渲染循环里不新建对象） */
  #takeSprite(): Sprite {
    const reused = this.#sprites.pop();
    if (reused !== undefined) {
      reused.visible = true;
      return reused;
    }
    const sprite = new Sprite(Texture.WHITE);
    sprite.eventMode = 'none';
    sprite.anchor.set(0.5);
    this.view.addChild(sprite);
    return sprite;
  }

  /** 每帧推进。死了的收回池子（不销毁，下一把接着用） */
  update(dtMs: number): void {
    if (this.#live.length === 0) return;
    const dt = Math.max(0, Math.min(dtMs, 100)) / 1000;
    for (let i = this.#live.length - 1; i >= 0; i--) {
      const p = this.#live[i];
      if (p === undefined) continue;
      p.age += dt;
      if (p.age >= p.life) {
        p.sprite.visible = false;
        this.#sprites.push(p.sprite);
        this.#live.splice(i, 1);
        continue;
      }
      // 位移和自转共用同一条包络：前半段"炸出去"，后半段基本停死（见 `travelSpeed`）
      const k = travelSpeed(p.age / p.life);
      p.sprite.x += p.vx * k * dt;
      p.sprite.y += p.vy * k * dt;
      p.sprite.rotation += p.spin * k * dt;
      p.sprite.alpha = particleAlpha(p.age / p.life);
    }
  }

  destroy(): void {
    this.#live.length = 0;
    this.#sprites.length = 0;
    /**
     * ⚠️ **顺序不能反**：先把 `Sprite` 从舞台上摘干净（`view.destroy`），
     * 再销毁贴图。反过来的话，那张贴图**此刻还绑在着色器上**
     * （渲染器刚用完它），Pixi 会一条一条地警告
     * "a textureSource was destroyed while still bound to a shader"。
     * 这一批是 26 张，一次重建就能刷出一屏警告。
     */
    this.view.destroy({ children: true });
    for (const texture of this.#pool) texture.destroy(true);
    this.#pool.length = 0;
  }
}
